import React, { useEffect, useState } from 'react';
import { 
  Card, Table, Badge, Button, Form, 
  Row, Col, Spinner, InputGroup,
  Pagination
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiFilter, FiEye, FiEdit } from 'react-icons/fi';
import casesService, { Case, CaseListResponse } from '../../services/cases.service';
import adminService, { User } from '../../services/admin.service';
import portfoliosService, { Portfolio } from '../../services/portfolios.service';

const CasesList: React.FC = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState<Case[]>([]);
  const [lawyers, setLawyers] = useState<User[]>([]);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [semaphoreFilter, setSemaphoreFilter] = useState('');
  const [lawyerFilter, setLawyerFilter] = useState('');
  const [portfolioFilter, setPortfolioFilter] = useState('');

  useEffect(() => {
    loadLawyers();
    portfoliosService.list().then(setPortfolios).catch(() => {});
  }, []);

  useEffect(() => {
    loadCases();
  }, [pagination.page, statusFilter, semaphoreFilter, lawyerFilter, portfolioFilter]);

  const loadLawyers = async () => {
    try {
      const users = await adminService.listUsers();
      setLawyers(users.filter(u => ['super_admin', 'coordinador', 'abogado'].includes(u.role_code || '')));
    } catch (error) {
      console.error('Error loading lawyers:', error);
    }
  };

  const loadCases = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.page,
        limit: pagination.limit
      };
      if (statusFilter) params.status = statusFilter;
      if (semaphoreFilter) params.semaphore = semaphoreFilter;
      if (lawyerFilter) params.lawyer_id = lawyerFilter;
      if (portfolioFilter) params.portfolio_id = portfolioFilter;
      if (search) params.search = search;

      const response = await casesService.list(params);
      setCases(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      }));
    } catch (error) {
      console.error('Error loading cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    loadCases();
  };

  const getSemaphoreBadge = (color: string) => {
    const colorMap: Record<string, string> = {
      green: 'semaphore-green',
      yellow: 'semaphore-yellow',
      red: 'semaphore-red'
    };
    const labelMap: Record<string, string> = {
      green: 'Normal',
      yellow: 'Precaución',
      red: 'Urgente'
    };
    return (
      <Badge className={colorMap[color] || 'bg-secondary'}>
        {labelMap[color] || color}
      </Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { bg: string; label: string }> = {
      active: { bg: 'primary', label: 'Activo' },
      open: { bg: 'info', label: 'Abierto' },
      in_progress: { bg: 'warning', label: 'En Proceso' },
      closed: { bg: 'success', label: 'Cerrado' },
      suspended: { bg: 'secondary', label: 'Suspendido' }
    };
    const config = statusMap[status] || { bg: 'secondary', label: status };
    return <Badge bg={config.bg}>{config.label}</Badge>;
  };

  const renderPagination = () => {
    const items = [];
    for (let i = 1; i <= pagination.totalPages; i++) {
      items.push(
        <Pagination.Item 
          key={i} 
          active={i === pagination.page}
          onClick={() => setPagination(prev => ({ ...prev, page: i }))}
        >
          {i}
        </Pagination.Item>
      );
    }
    return (
      <Pagination className="mb-0">
        <Pagination.First 
          disabled={pagination.page === 1}
          onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
        />
        <Pagination.Prev 
          disabled={pagination.page === 1}
          onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
        />
        {items}
        <Pagination.Next 
          disabled={pagination.page === pagination.totalPages}
          onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
        />
        <Pagination.Last 
          disabled={pagination.page === pagination.totalPages}
          onClick={() => setPagination(prev => ({ ...prev, page: pagination.totalPages }))}
        />
      </Pagination>
    );
  };

  return (
    <div className="cases-list">
      <div className="page-header">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h4 className="page-title mb-1">Casos</h4>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to="/">Inicio</Link></li>
                <li className="breadcrumb-item active">Casos</li>
              </ol>
            </nav>
          </div>
          <Button 
            variant="primary" 
            onClick={() => navigate('/cases/new')}
          >
            <FiPlus className="me-2" />
            Nuevo Caso
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleSearch}>
            <Row className="g-3 align-items-end">
              <Col md={4}>
                <Form.Label>Buscar</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FiSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Radicado, título, cliente..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Label>Estado</Form.Label>
                <Form.Select 
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="active">Activo</option>
                  <option value="suspended">Suspendido</option>
                  <option value="closed">Cerrado</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label>Semáforo</Form.Label>
                <Form.Select 
                  value={semaphoreFilter}
                  onChange={(e) => setSemaphoreFilter(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="green">Normal</option>
                  <option value="yellow">Precaución</option>
                  <option value="red">Urgente</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label>Abogado</Form.Label>
                <Form.Select 
                  value={lawyerFilter}
                  onChange={(e) => setLawyerFilter(e.target.value)}
                >
                  <option value="">Todos</option>
                  {lawyers.map(lawyer => (
                    <option key={lawyer.id} value={lawyer.id}>
                      {lawyer.full_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label>Cartera</Form.Label>
                <Form.Select
                  value={portfolioFilter}
                  onChange={(e) => setPortfolioFilter(e.target.value)}
                >
                  <option value="">Todas</option>
                  {portfolios.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={1}>
                <Button type="submit" variant="secondary" className="w-100">
                  <FiFilter />
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Tabla de casos */}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              <Table hover responsive className="mb-0 table-sm">
                <thead className="bg-light">
                  <tr>
                    <th style={{width: '180px'}}>Radicado</th>
                    <th>Cliente</th>
                    <th>Demandante vs Demandado</th>
                    <th style={{width: '120px'}}>Estado Proceso</th>
                    <th style={{width: '100px'}}>Estado</th>
                    <th style={{width: '80px'}} className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cases.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5 text-muted">
                        No se encontraron casos
                      </td>
                    </tr>
                  ) : (
                    cases.map((caso) => (
                      <tr key={caso.id}>
                        <td>
                          <Link to={`/cases/${caso.id}`} className="fw-semibold text-primary" style={{fontSize: '0.85rem'}}>
                            {caso.case_number?.substring(0, 20)}{caso.case_number?.length > 20 ? '...' : ''}
                          </Link>
                        </td>
                        <td>
                          <div className="text-truncate" style={{maxWidth: '150px'}} title={caso.client_name}>
                            {caso.client_name || 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div className="text-truncate" style={{maxWidth: '250px'}} title={`${caso.plaintiff || ''} vs ${caso.defendant || ''}`}>
                            <small>{caso.plaintiff || '-'} <strong>vs</strong> {caso.defendant || '-'}</small>
                          </div>
                        </td>
                        <td>
                          <small className="text-muted">{caso.process_status || '-'}</small>
                        </td>
                        <td>{getStatusBadge(caso.status)}</td>
                        <td className="text-end">
                          <Button 
                            variant="light" 
                            size="sm" 
                            className="me-1"
                            onClick={() => navigate(`/cases/${caso.id}`)}
                          >
                            <FiEye />
                          </Button>
                          <Button 
                            variant="light" 
                            size="sm"
                            onClick={() => navigate(`/cases/${caso.id}/edit`)}
                          >
                            <FiEdit />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <small className="text-muted">
                    Mostrando {cases.length} de {pagination.total} casos
                  </small>
                  {renderPagination()}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default CasesList;
